import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api_url } from '../constants';
import {
    Box,
    Paper,
    Typography,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert,
    Snackbar,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Settings = () => {
    const navigate = useNavigate();
    const [autoApprove, setAutoApprove] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [lastUpdated, setLastUpdated] = useState(null);

    // Fetch current settings on component mount
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${api_url}/api/settings`);
            if (response.data) {
                setAutoApprove(Boolean(response.data.autoApprovePosts));
                if (response.data.updatedAt) {
                    setLastUpdated(new Date(response.data.updatedAt).toLocaleString());
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            showNotification('Failed to load settings. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (event) => {
        const newValue = event.target.checked;
        setAutoApprove(newValue);
        await saveSetting(newValue);
    };

    const saveSetting = async (valueToSave) => {
        setSaving(true);
        try {
            const response = await axios.put(`${api_url}/api/settings`, {
                autoApprovePosts: valueToSave
            });
            if (response.data && response.data.setting) {
                setAutoApprove(Boolean(response.data.setting.autoApprovePosts));
                if (response.data.setting.updatedAt) {
                    setLastUpdated(new Date(response.data.setting.updatedAt).toLocaleString());
                }
            }
            showNotification(
                valueToSave
                    ? 'Auto-approve enabled: New posts will now be approved automatically!'
                    : 'Auto-approve disabled: New posts will now go to Pending for review.',
                'success'
            );
        } catch (error) {
            console.error('Error saving settings:', error);
            // Revert state on failure
            setAutoApprove(!valueToSave);
            showNotification('Failed to update settings. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    return (
        <Box sx={{ maxWidth: 850, mx: 'auto', p: 1 }}>
            {/* Page Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="600" color="primary.main">
                    Settings
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Configure general platform behavior and post moderation workflow.
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={3}>
                    {/* Post Moderation Card */}
                    <Card elevation={2} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="h6" fontWeight="600">
                                    Post Moderation & Approval
                                </Typography>
                                <Chip
                                    label={autoApprove ? 'Auto-Approve Enabled' : 'Pending Review Required (Default)'}
                                    color={autoApprove ? 'success' : 'warning'}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Control whether newly submitted posts from users are immediately approved or placed in pending review.
                            </Typography>

                            <Divider sx={{ mb: 3 }} />

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: autoApprove ? 'rgba(46, 125, 50, 0.04)' : 'rgba(237, 108, 2, 0.04)',
                                p: 2.5,
                                borderRadius: 2,
                                border: autoApprove ? '1px solid rgba(46, 125, 50, 0.2)' : '1px solid rgba(237, 108, 2, 0.2)'
                            }}>
                                <Box sx={{ pr: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                                        Auto-Approve New Posts
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {autoApprove ? (
                                            <span style={{ color: '#2e7d32' }}>
                                                Enabled: When users submit a post, it is immediately marked as <strong>Approved</strong>.
                                            </span>
                                        ) : (
                                            <span style={{ color: '#ed6c02' }}>
                                                Default: When users submit a post, it is marked as <strong>Pending</strong> and requires admin approval.
                                            </span>
                                        )}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {saving && <CircularProgress size={22} />}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={autoApprove}
                                                onChange={handleToggle}
                                                disabled={saving}
                                                color="success"
                                                inputProps={{ 'aria-label': 'toggle auto-approve' }}
                                            />
                                        }
                                        label=""
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                            </Box>

                            {lastUpdated && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
                                    Last updated: {lastUpdated}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    {/* Quick Link Card */}
                    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="600">
                                    Need to review pending posts?
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    View and manage posts currently awaiting admin approval.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/posts/pending')}
                                sx={{ textTransform: 'none' }}
                            >
                                View Pending Posts
                            </Button>
                        </Box>
                    </Paper>
                </Stack>
            )}

            {/* Notification Toast */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Settings;
